import type { PictureSources } from '@/types/Media'

type ImageSourceEntry = {
  fallback: string
  fallbackType: string
  sources: Array<{ type: string; srcset: string }>
}

type GlobResult = Record<string, string>

type RegistryConfig = {
  files: GlobResult
  root: string
}

// Map file extensions to MIME types. Include AVIF for future assets.
const mimeByExtension: Record<string, string> = {
  avif: 'image/avif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp'
}

// Order of source types for <picture>. Lower numbers are preferred earlier.
const typeOrder: Record<string, number> = {
  'image/avif': 0,
  'image/webp': 1,
  'image/jpeg': 2,
  'image/png': 3
}

// Single image directory with optimized images (JPG/PNG + WebP/AVIF variants)
const images = import.meta.glob<string>(
  '/src/assets/images/**/*.{jpg,jpeg,png,webp,avif}',
  { eager: true, import: 'default' }
)

const registryConfigs: RegistryConfig[] = [
  { files: images, root: '/src/assets/images/' }
]

const imageRegistry: Record<string, PictureSources> = buildRegistry()

function buildRegistry(): Record<string, PictureSources> {
  const working: Record<string, ImageSourceEntry> = {}

  for (const config of registryConfigs) {
    for (const [path, src] of Object.entries(config.files)) {
      const { key, extension } = normalisePath(path, config.root)
      if (!extension) continue
      const type = mimeByExtension[extension]
      if (!type) continue

      // Normalise key to lower‑case for case‑insensitive look‑ups.
      const lcKey = key.toLowerCase()
      const entry = working[lcKey] ?? {
        fallback: '',
        fallbackType: '',
        sources: []
      }

      // Replace existing source of same MIME type to keep the most specific (higher priority) asset.
      entry.sources = entry.sources.filter((source) => source.type !== type)
      entry.sources.push({ type, srcset: src })

      // Prefer non‑webp (and non‑avif) assets as fallback, but always ensure a fallback exists.
      const isPreferredFallback = type !== 'image/webp' && type !== 'image/avif'
      if (isPreferredFallback || entry.fallback === '') {
        entry.fallback = src
        entry.fallbackType = type
      }

      working[lcKey] = entry
    }
  }

  const built: Record<string, PictureSources> = {}

  for (const [key, entry] of Object.entries(working)) {
    const sources = entry.sources
      .slice()
      .sort((a, b) => (typeOrder[a.type] ?? 10) - (typeOrder[b.type] ?? 10))
    built[key] = {
      fallback: entry.fallback,
      sources
    }
  }

  return built
}

// Normalise a file path to a lookup key and extension.
function normalisePath(path: string, root: string): { key: string; extension: string | null } {
  let relative = path.startsWith(root) ? path.slice(root.length) : path
  relative = relative.replace(/^\/+/, '')
  relative = relative.replace(/\\/g, '/')
  const dotIndex = relative.lastIndexOf('.')
  if (dotIndex === -1) {
    return { key: relative, extension: null }
  }
  const extension = relative.slice(dotIndex + 1).toLowerCase()
  const key = relative.slice(0, dotIndex).toLowerCase()
  return { key, extension }
}

export function getPictureSources(key: string): PictureSources | undefined {
  const normalised = normaliseLookupKey(key)
  return imageRegistry[normalised]
}

export function requirePictureSources(key: string): PictureSources {
  const normalised = normaliseLookupKey(key)
  const entry = imageRegistry[normalised]
  if (!entry) {
    throw new Error(`Image assets not found for key: ${key}`)
  }
  return entry
}

export function listImageKeys(prefix?: string): string[] {
  const keys = Object.keys(imageRegistry)
  if (!prefix) return keys
  const safePrefix = normaliseLookupKey(prefix)
  return keys.filter((key) => key.startsWith(`${safePrefix}`))
}

// Normalise a lookup key: strip slashes, backslashes, extension and lower‑case.
function normaliseLookupKey(key: string): string {
  const trimmed = key.replace(/^\/+|\/+$/g, '').replace(/\\/g, '/').toLowerCase()
  return trimmed.replace(/\.(png|jpe?g|webp|avif)$/i, '')
}
